import React, { useState, useEffect } from 'react';
import {
  Search,
  Filter,
  DollarSign,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  User,
  Download,
  Eye,
  Trophy,
  ChevronDown,
  ChevronUp,
  X,
  Copy,
  RefreshCw
} from 'lucide-react';

const TeamWithdrawalReport = () => {
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: 'all',
    search: ''
  });
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [expandedRows, setExpandedRows] = useState([]);
  const [stats, setStats] = useState({
    totalAmount: 0,
    totalCount: 0,
    pendingCount: 0,
    completedCount: 0
  });

  // Fetch data
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const usersRes = await fetch('https://aerovolt-db464-default-rtdb.firebaseio.com/users.json').then(res => res.json());

      const usersByDate = usersRes || {};
      const withdrawalsArray = [];

      // Process claimed rewards stored on user records (TeamSalesRewards + LeaderRewards)
      Object.entries(usersByDate).forEach(([dateKey, usersInDate]) => {
        if (!usersInDate) return;

        Object.entries(usersInDate).forEach(([dbKey, user]) => {
          if (!user) return;

          const memberId = user.sponsorId || user.userId || dbKey;
          const memberName = user.name || user.fullName || 'Unknown';
          const sponsorId = user.sponserId || user.sponsorId || user.uplineSponsorId || 'N/A';

          if (Array.isArray(user.teamRewards)) {
            user.teamRewards.forEach((reward, idx) => {
              if (!reward || reward.status !== 'claimed') return;

              const amount = parseFloat(reward.cashAmount) || 0;
              const trackId = reward.rewardId || `TS_${memberId}_${idx + 1}`;

              withdrawalsArray.push({
                id: trackId,
                trackId,
                memberId,
                memberName,
                sponsorId,
                amount,
                netAmount: amount,
                status: 'completed',
                paymentMethod: 'Reward Wallet',
                requestDate: reward.claimedAt || new Date().toISOString(),
                remarks: `Team Sales Reward: ${reward.rewardName || ''}`,
                rewardAmount: amount,
                rewardType: 'team_sales',
                walletType: reward.walletType || 'working',
                userDetails: user,
                dataSource: `users/${dateKey}/${dbKey}/teamRewards`
              });
            });
          }

          if (Array.isArray(user.leaderRewards)) {
            user.leaderRewards.forEach((reward, idx) => {
              if (!reward || reward.status !== 'claimed') return;

              const amount = parseFloat(reward.cashAmount) || 0;
              const trackId = reward.rewardId || `LR_${memberId}_${idx + 1}`;

              withdrawalsArray.push({
                id: trackId,
                trackId,
                memberId,
                memberName,
                sponsorId,
                amount,
                netAmount: amount,
                status: 'completed',
                paymentMethod: 'Reward Wallet',
                requestDate: reward.claimedAt || new Date().toISOString(),
                remarks: `Leader Reward${reward.timeframe ? ` (${reward.timeframe})` : ''}: ${reward.rewardName || ''}`,
                rewardAmount: amount,
                rewardType: 'leader',
                walletType: reward.walletType || 'working',
                userDetails: user,
                dataSource: `users/${dateKey}/${dbKey}/leaderRewards`
              });
            });
          }
        });
      });

      // Calculate stats
      const totalAmount = withdrawalsArray.reduce((sum, w) => sum + w.amount, 0);
      const completedCount = withdrawalsArray.filter(w => w.status === 'completed').length;
      
      setStats({
        totalAmount,
        totalCount: withdrawalsArray.length,
        pendingCount: 0, // TeamSalesRewards are always completed when claimed
        completedCount
      });

      // Sort by date
      withdrawalsArray.sort((a, b) => new Date(b.requestDate) - new Date(a.requestDate));
      setWithdrawals(withdrawalsArray);

    } catch (err) {
      console.error('Error:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const filteredWithdrawals = withdrawals.filter(w => {
    const matchesStatus = filters.status === 'all' || w.status === filters.status;
    const matchesSearch = !filters.search || 
      w.trackId?.toLowerCase().includes(filters.search.toLowerCase()) ||
      w.memberName?.toLowerCase().includes(filters.search.toLowerCase()) ||
      w.memberId?.toLowerCase().includes(filters.search.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const getStatusBadge = (status) => {
    const config = {
      completed: { color: 'text-green-400', bg: 'bg-green-500/20', border: 'border-green-500/30', icon: CheckCircle, label: 'Completed' },
      pending: { color: 'text-yellow-400', bg: 'bg-yellow-500/20', border: 'border-yellow-500/30', icon: AlertCircle, label: 'Pending' },
    };
    
    const { color, bg, border, icon: Icon, label } = config[status] || config.completed;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${bg} ${border} ${color}`}>
        <Icon className="h-3 w-3 mr-1.5" />
        {label}
      </span>
    );
  };

  const toggleRowExpansion = (id) => {
    setExpandedRows(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const openModal = (withdrawal) => {
    setSelectedWithdrawal(withdrawal);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedWithdrawal(null);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const exportToCSV = () => {
    const headers = ['Track ID', 'Member', 'Amount ($)', 'Status', 'Date', 'Reward Type'];
    const csvData = filteredWithdrawals.map(w => [
      w.trackId,
      w.memberName,
      w.amount.toFixed(2),
      w.status,
      new Date(w.requestDate).toLocaleDateString(),
      w.rewardType
    ]);
    
    const csvContent = [headers.join(','), ...csvData.map(row => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `team_withdrawals_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto pt-8 px-4">
        <div className="text-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading withdrawals...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto pt-8 px-4">
      {/* Header */}
      <div className="mb-6">
  <h1 className="text-2xl font-bold text-foreground mb-2">Sales Commission Withdrawals</h1>
        <p className="text-muted-foreground">sales reward withdrawals</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="card-glow p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-muted-foreground text-sm mb-1">Total Withdrawals</div>
              <div className="text-2xl font-bold text-foreground">${stats.totalAmount.toFixed(2)}</div>
            </div>
            <DollarSign className="h-5 w-5 text-primary" />
          </div>
        </div>
        
        <div className="card-glow p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-muted-foreground text-sm mb-1">Total Count</div>
              <div className="text-2xl font-bold text-foreground">{stats.totalCount}</div>
            </div>
            <Trophy className="h-5 w-5 text-yellow-400" />
          </div>
        </div>
        
        <div className="card-glow p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-muted-foreground text-sm mb-1">Completed</div>
              <div className="text-2xl font-bold text-green-400">{stats.completedCount}</div>
            </div>
            <CheckCircle className="h-5 w-5 text-green-400" />
          </div>
        </div>
        
        <div className="card-glow p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-muted-foreground text-sm mb-1">Avg Amount</div>
              <div className="text-2xl font-bold text-blue-400">
                ${stats.totalCount > 0 ? (stats.totalAmount / stats.totalCount).toFixed(2) : '0.00'}
              </div>
            </div>
            <DollarSign className="h-5 w-5 text-blue-400" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card-glow p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              placeholder="Search by Track ID, Member ID or Name"
              className="w-full px-4 py-2 bg-secondary/80 border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary"
            />
          </div>
          <div className="w-full md:w-48">
            <select
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              className="w-full px-4 py-2 bg-secondary/80 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary"
            >
              <option value="all">All Status</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
            </select>
          </div>
          <div className="flex gap-2">
            <button
              onClick={fetchData}
              className="px-4 py-2 bg-secondary hover:bg-secondary/80 rounded-lg border border-border"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
            <button
              onClick={exportToCSV}
              className="btn-electric px-4 py-2 rounded-lg flex items-center"
            >
              <Download className="h-4 w-4 mr-2" />
              Export
            </button>
          </div>
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden lg:block card-glow overflow-hidden mb-6">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-secondary/50">
              <tr>
                <th className="py-3 px-4 text-left text-muted-foreground font-semibold">Track ID</th>
                <th className="py-3 px-4 text-left text-muted-foreground font-semibold">Member</th>
                <th className="py-3 px-4 text-left text-muted-foreground font-semibold">Amount</th>
                <th className="py-3 px-4 text-left text-muted-foreground font-semibold">Status</th>
                <th className="py-3 px-4 text-left text-muted-foreground font-semibold">Date</th>
                <th className="py-3 px-4 text-left text-muted-foreground font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/30">
              {filteredWithdrawals.map(withdrawal => (
                <tr key={withdrawal.id} className="hover:bg-secondary/20">
                  <td className="py-3 px-4">
                    <div className="font-mono text-sm text-foreground">{withdrawal.trackId}</div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="font-medium text-foreground">{withdrawal.memberName}</div>
                    <div className="text-xs text-muted-foreground">{withdrawal.memberId}</div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="font-bold text-foreground">${withdrawal.amount.toFixed(2)}</div>
                  </td>
                  <td className="py-3 px-4">
                    {getStatusBadge(withdrawal.status)}
                  </td>
                  <td className="py-3 px-4">
                    {new Date(withdrawal.requestDate).toLocaleDateString()}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex gap-2">
                      <button
                        onClick={() => openModal(withdrawal)}
                        className="p-2 hover:bg-secondary rounded-lg"
                      >
                        <Eye className="h-4 w-4 text-primary" />
                      </button>
                      <button
                        onClick={() => copyToClipboard(withdrawal.trackId)}
                        className="p-2 hover:bg-secondary rounded-lg"
                      >
                        <Copy className="h-4 w-4 text-muted-foreground" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden space-y-4">
        {filteredWithdrawals.map(withdrawal => (
          <div key={withdrawal.id} className="card-glow p-4">
            <div className="flex justify-between items-start mb-3">
              <div>
                <div className="font-medium text-foreground">{withdrawal.memberName}</div>
                <div className="text-xs text-muted-foreground">{withdrawal.memberId}</div>
              </div>
              <div className="text-right">
                <div className="font-bold text-foreground">${withdrawal.amount.toFixed(2)}</div>
                {getStatusBadge(withdrawal.status)}
              </div>
            </div>
            
            <div className="text-sm text-muted-foreground mb-3">
              <div>Track ID: {withdrawal.trackId}</div>
              <div>Date: {new Date(withdrawal.requestDate).toLocaleDateString()}</div>
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={() => openModal(withdrawal)}
                className="flex-1 py-2 bg-secondary hover:bg-secondary/80 rounded-lg flex items-center justify-center"
              >
                <Eye className="h-4 w-4 mr-2" />
                View Details
              </button>
              <button
                onClick={() => copyToClipboard(withdrawal.trackId)}
                className="px-4 py-2 bg-secondary hover:bg-secondary/80 rounded-lg"
              >
                <Copy className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredWithdrawals.length === 0 && (
        <div className="text-center py-12 card-glow">
          <div className="text-4xl mb-4">💸</div>
          <h3 className="text-lg font-semibold text-foreground mb-2">No Withdrawals Found</h3>
          <p className="text-muted-foreground">Try adjusting your search filters</p>
        </div>
      )}

      {/* Modal */}
      {showModal && selectedWithdrawal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
          <div className="bg-secondary rounded-xl w-full max-w-md max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b border-border flex justify-between items-center">
              <h3 className="font-semibold text-foreground">Withdrawal Details</h3>
              <button onClick={closeModal} className="p-1">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              <div className="space-y-4">
                <div>
                  <div className="text-muted-foreground text-sm mb-1">Track ID</div>
                  <div className="font-mono text-foreground">{selectedWithdrawal.trackId}</div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-muted-foreground text-sm mb-1">Member</div>
                    <div className="text-foreground">{selectedWithdrawal.memberName}</div>
                    <div className="text-xs text-muted-foreground">{selectedWithdrawal.memberId}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground text-sm mb-1">Sponsor ID</div>
                    <div className="text-foreground">{selectedWithdrawal.sponsorId}</div>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-muted-foreground text-sm mb-1">Amount</div>
                    <div className="text-2xl font-bold text-foreground">${selectedWithdrawal.amount.toFixed(2)}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground text-sm mb-1">Status</div>
                    {getStatusBadge(selectedWithdrawal.status)}
                  </div>
                </div>
                
                <div>
                  <div className="text-muted-foreground text-sm mb-1">Request Date</div>
                  <div className="text-foreground">{new Date(selectedWithdrawal.requestDate).toLocaleString()}</div>
                </div>
                
                <div>
                  <div className="text-muted-foreground text-sm mb-1">Wallet Type</div>
                  <div className="text-foreground">{selectedWithdrawal.walletType}</div>
                </div>
                
                {selectedWithdrawal.walletBalanceBefore && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-muted-foreground text-sm mb-1">Balance Before</div>
                      <div className="text-foreground">${selectedWithdrawal.walletBalanceBefore.toFixed(2)}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground text-sm mb-1">Balance After</div>
                      <div className="text-green-400">${selectedWithdrawal.walletBalanceAfter.toFixed(2)}</div>
                    </div>
                  </div>
                )}
                
                <div>
                  <div className="text-muted-foreground text-sm mb-1">Remarks</div>
                  <div className="text-foreground">{selectedWithdrawal.remarks}</div>
                </div>
              </div>
            </div>
            
            <div className="p-4 border-t border-border">
              <button
                onClick={closeModal}
                className="w-full py-2 bg-primary text-white rounded-lg"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamWithdrawalReport;

